import {
    Alert,
    CircularProgress,
    Divider,
    Grid,
    InputAdornment,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import Button from "@mui/material/Button";
import React, {ChangeEvent, useCallback, useEffect, useMemo, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import toast from "react-hot-toast";
import DialogAddress, {AddressFormData} from "../components-company-data/DialogAddress";
import ReplayIcon from '@mui/icons-material/Replay';
import {Icon} from "@iconify/react";
import {SolicitationClientFormDataFields} from "../../StepSuretiesData";
import AddressFormContentData from "../AddressFormContentData";
import {
    Client,
    ClientAddress, ClientAddressFields,
    ClientAddressTypes,
    ClientFields,
    ClientRelationshipInsert,
    ClientRelationshipInsertFields,
    ClientRelationshipTypeClassification, ClientRelationshipViewFields,
    ClientType,
    CoOwnerRelationshipWithoutSolicitation,
    CoOwnerRelationshipWithoutSolicitationFields
} from "../../../../../types/client/clientData";
import {MaritalStates, Modules} from "../../../../../types/generalData";
import {DNISchema, NotRequiredSchema, RequiredSchema} from "../../../../../utils/validations";
import {EntityWithIdFields} from "../../../../../types/baseEntities";
import {SolicitationClientFields} from "../../../../../types/solicitation/solicitationData";
import {HttpCacheClient} from "../../../../../http/cache/client/httpCacheClient";
import ClientSummaryDialogCard
    from "../../../../../@core/layouts/components/horizontal/app-bar-content/ClientSummaryDialogCard";
import {AsyncSelect, ControlledDatePicker, ControlledTextField} from "../../../../../components/forms";
import {HttpClient, HttpClientAddress} from "../../../../../http/client/httpClient";
import {ClientRelationshipClassificationCodes, ClientRelationshipCodes} from "../../../../../types/client/clientEnums";


interface CoOwnersDetailDialogFormProps {
    client: Client,
    onFinishSave: (coOwner: CoOwnerRelationshipWithoutSolicitation) => void,
    onBack: () => void,
    coOwnerToEdit?: CoOwnerRelationshipWithoutSolicitation,
    setCoOwners? : (list: CoOwnerRelationshipWithoutSolicitation[]) => void
}

const CoOwnersDetailDialogForm = ({client, onFinishSave, onBack, coOwnerToEdit} : CoOwnersDetailDialogFormProps) => {
    const [value, setValuePerc] = useState<number>(coOwnerToEdit ? coOwnerToEdit[ClientRelationshipViewFields.Participation] : 0);
    const [error, setError] = useState('');
    const [editAddress, setEditAddress] = useState<boolean>(false)
    const [addressInDialog, setAddressInDialog] = useState<ClientAddress | undefined>(undefined)
    const [fiance, setFiance] = useState<Client | undefined>(undefined)
    const [loadingCuit, setLoadingCuit] = useState<boolean>(false)
    const [cuit, setCuit] = useState<string>('')
    const [relationshipTypes, setRelationshipTypes] = useState<ClientRelationshipTypeClassification[]>([]);
    const [loading, setLoading] = useState<boolean>(false)

    const onCloseDialog = () => {
        setEditAddress(false)
        setAddressInDialog(undefined)
    }
    

    //TO DO: los datos de afip no se pueden sobreescribir, por ende lo de agregar un conyuge es medio confuso.
    //          en el update faltaria validar eso


    const ClientFormSchema = useMemo(() => yup.object().shape({
        //[ClientFields.CUIT]: CuitCuilSchema.required('Campo obligatorio'),
        //[ClientFields.BusinessName]: RequiredSchema,
        [ClientFields.IdentificationNumber]:
            client?.[SolicitationClientFields.ClientTypeCode] === ClientType.Fisica
                ? DNISchema.required('Campo obligatorio')
                : NotRequiredSchema,
        [ClientFields.AfipResponsibilityTypeCode]: RequiredSchema,
        [ClientFields.BirthDate]: RequiredSchema,
        [ClientFields.MaritalStatusCode]: client?.[SolicitationClientFields.ClientTypeCode] === ClientType.Fisica ? RequiredSchema : NotRequiredSchema
    }), [client])

    const methods = useForm<CoOwnerRelationshipWithoutSolicitation>({
        resolver: yupResolver(ClientFormSchema),
        defaultValues: {
            [ClientFields.CUIT]: client[ClientFields.CUIT],
            [ClientFields.BusinessName]: client[ClientFields.BusinessName],
            [ClientFields.MaritalStatusCode]: client[ClientFields.MaritalStatusCode] || coOwnerToEdit?.[ClientFields.MaritalStatusCode],
            [ClientFields.AfipResponsibilityTypeCode]: client[ClientFields.AfipResponsibilityTypeCode],
            [ClientFields.IdentificationNumber]: client[ClientFields.IdentificationNumber],
            [ClientFields.GenderCode]: client[ClientFields.GenderCode],
            [ClientFields.ClientTypeCode]: client[ClientFields.ClientTypeCode],
            [ClientFields.ClientClassificationCode]: client[ClientFields.ClientClassificationCode],
            [ClientFields.Id]: client[EntityWithIdFields.Id],
            [ClientFields.BirthDate]: new Date(client[ClientFields.BirthDate]),
            [EntityWithIdFields.Id]: coOwnerToEdit?.[EntityWithIdFields.Id],
            [SolicitationClientFields.FianceName]: coOwnerToEdit?.[SolicitationClientFields.FianceName],
            [SolicitationClientFields.FianceCuit]: coOwnerToEdit?.[SolicitationClientFields.FianceCuit],
            [ClientRelationshipViewFields.Participation]: coOwnerToEdit?.[ClientRelationshipViewFields.Participation],
            [ClientRelationshipViewFields.RelationshipTypeCode]: coOwnerToEdit?.[ClientRelationshipViewFields.RelationshipTypeCode]
        }
    })

    const watchMaritalStatus = methods.watch(ClientFields.MaritalStatusCode)
    const loadAddresses = () => {
        setLoading(true)
        HttpClientAddress.getByIdClient(client[EntityWithIdFields.Id]).then((r) => {
            const actualAddress = client[ClientFields.ClientTypeCode] === ClientType.Juridica ?
                r.find((a) => a[ClientAddressFields.AddressTypeCode] === ClientAddressTypes.Legal)
                :
                r.find((a) => a[ClientAddressFields.AddressTypeCode] === ClientAddressTypes.Real)

            methods.setValue(CoOwnerRelationshipWithoutSolicitationFields.Address, actualAddress as AddressFormData)
        }).finally(() => setLoading(false))
    }
    const onSubmitDialog = () => {
        toast.success("La dirección fue guardada con éxito")
        loadAddresses()
    }


    useEffect(() => {
        if (coOwnerToEdit && (coOwnerToEdit[ClientFields.MaritalStatusCode] === MaritalStates.Casado || coOwnerToEdit[ClientFields.MaritalStatusCode] === MaritalStates.Convivientes)){
            setLoadingCuit(true)
            HttpClient.sincronizeClientByCUIT(coOwnerToEdit[ClientFields.PartnerCuit], Modules.SolicitationOnboarding).then((r) => {
                setLoadingCuit(false)
                if (r.tieneError) {
                    toast.error(r.descripcionError)
                } else {
                    setFiance(r.data)
                }
            })
        }
        
        if (client && !coOwnerToEdit && (client[ClientFields.MaritalStatusCode] === MaritalStates.Casado || client[ClientFields.MaritalStatusCode] === MaritalStates.Convivientes)) {
            setLoadingCuit(true)
            HttpClient.sincronizeClientByCUIT(client[ClientFields.PartnerCuit], Modules.SolicitationOnboarding).then((r) => {
                setLoadingCuit(false)
                if (r.tieneError) {
                    toast.error(r.descripcionError)
                } else {
                    setFiance(r.data)
                }
            })
        }
    }, [client, coOwnerToEdit])
    const onSubmit = (data: CoOwnerRelationshipWithoutSolicitation) => {
        
        const relsCoOwner: ClientRelationshipInsert[] = [
            {
                [ClientRelationshipInsertFields.Participation]: data[ClientRelationshipViewFields.Participation] || 0,
                [ClientRelationshipInsertFields.RelationshipTypeCode]: data[ClientRelationshipViewFields.RelationshipTypeCode],
                [ClientRelationshipInsertFields.RelationshipClassificationTypeCode]: ClientRelationshipClassificationCodes.CoOwners,
                [ClientRelationshipInsertFields.Signer]: false
            }]

        const coOwnerData: CoOwnerRelationshipWithoutSolicitation = {
            ...data,
            [ClientRelationshipViewFields.RelationshipTypeCode]: data[ClientRelationshipViewFields.RelationshipTypeCode],
            [CoOwnerRelationshipWithoutSolicitationFields.CoOwnerRelationship]: relsCoOwner
        }
                toast.success("El cotitular se ha guardado correctamente")
                if(fiance && fiance[ClientFields.ClientTypeCode] === ClientType.Fisica) {
                    const relsMarital: ClientRelationshipInsert[] = [
                        {
                            [ClientRelationshipInsertFields.Participation]: 0,
                            [ClientRelationshipInsertFields.RelationshipTypeCode]: ClientRelationshipCodes.Fiance,
                            [ClientRelationshipInsertFields.RelationshipClassificationTypeCode]: ClientRelationshipClassificationCodes.RelatedPeople,
                            [ClientRelationshipInsertFields.Signer]: false
                        }
                    ]
                    const coOwnerWithMaritageData = {
                        ...coOwnerData,
                        [CoOwnerRelationshipWithoutSolicitationFields.FianceId]: fiance[EntityWithIdFields.Id],
                        [CoOwnerRelationshipWithoutSolicitationFields.FianceCuit]: fiance[ClientFields.CUIT],
                        [CoOwnerRelationshipWithoutSolicitationFields.FianceName]: fiance[ClientFields.BusinessName],
                        [CoOwnerRelationshipWithoutSolicitationFields.MaritalRelationship]: relsMarital
                    }
                    onFinishSave(coOwnerWithMaritageData)
                }else{
                    onFinishSave(coOwnerData)
                }

        if (data[CoOwnerRelationshipWithoutSolicitationFields.Address]) {
            const addressToInsert: AddressFormData = {
                ...data[CoOwnerRelationshipWithoutSolicitationFields.Address],
                [ClientAddressFields.AddressTypeCode]: client[ClientFields.ClientTypeCode] === ClientType.Fisica ? ClientAddressTypes.Real : ClientAddressTypes.Legal,
                [ClientAddressFields.CountryCode]: 1,
                [ClientAddressFields.Verified]: true
            }

            !addressToInsert[ClientAddressFields.Id]
                ? HttpClientAddress.insert(client[EntityWithIdFields.Id], addressToInsert as ClientAddress).then(() => {
                    console.log("")
                })
                : HttpClientAddress.update(client[EntityWithIdFields.Id], addressToInsert as ClientAddress).then(() => {
                    console.log("")
                });
        }
                
    }

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setValuePerc(parseInt(inputValue));

        if (parseInt(inputValue) === 0 || client?.[ClientFields.ClientTypeCode] === ClientType.Juridica || parseInt(inputValue)<= 100) {
            setError('');
        } else {
            setError('Número grande');
        }
    };

    useEffect(() => {
        methods.setValue(ClientRelationshipViewFields.Participation, value)
    }, [value])

    const loadGenderTypes = useCallback(() => {
        return HttpCacheClient.getGeneros();
    }, [])

    const loadMaritalStatusTypes = useCallback(() => {
        return HttpCacheClient.getEstadosCiviles();
    }, [])

    const loadResponsibilityTypes = useCallback(() => {
        return HttpCacheClient.getResponsableTipo();
    }, [])

    const loadClientClassifications = useCallback(() => {
        return HttpCacheClient.getClienteClasificaciones()
    }, [])

    const loadRelationshipTypes = useCallback(() => {
        HttpCacheClient.getRelacionTipos(5, client[ClientFields.ClientClassificationCode])
            .then(relationshipList => {
                setRelationshipTypes(relationshipList);
            })
    }, [])

    useEffect(() => {
        loadAddresses()
    }, [client])

    useEffect(() => {
        loadRelationshipTypes()
    }, [])

    const handleChangeCuit = (e: ChangeEvent<HTMLInputElement>) => {
        setCuit(e.target.value)
    }
    const onCancel = () => setCuit('')

    const handleClick = () => {
        setLoadingCuit(true)
        HttpClient.sincronizeClientByCUIT(cuit, Modules.SolicitationOnboarding).then((r) => {
            setLoadingCuit(false)
            if (r.tieneError) {
                toast.error(r.descripcionError)
            } else {
                setFiance(r.data)
            }
        }).catch(() => {
            toast.error('El CUIT/CUIL ingresado es inválido')
            setLoadingCuit(false)
        })
    }

    return (
        <Grid item xs={12} container spacing={2}>
            <Grid item xs={12}>
                <Typography fontSize={16} fontWeight={600}>Datos del cotitular</Typography>
            </Grid>
            <Grid item xs={12}>
                <Divider/>
            </Grid>
            <Grid item xs={12} mb={3}><ClientSummaryDialogCard client={client}/></Grid>
            <Grid item xs={6}>
                <AsyncSelect control={methods.control} label="Responsabilidad AFIP"
                             defaultValue={client?.[ClientFields.AfipResponsibilityTypeCode]}
                             loadOptions={loadResponsibilityTypes}
                             name={ClientFields.AfipResponsibilityTypeCode}
                             fullWidth
                />
            </Grid>
            {
                (client?.[ClientFields.ClientTypeCode] === ClientType.Fisica)
                &&
                <React.Fragment>
                    <Grid item xs={2}>
                        <ControlledTextField control={methods.control} label="Documento"
                                             name={ClientFields.IdentificationNumber}
                                             fullWidth
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <AsyncSelect control={methods.control} label="Género" loadOptions={loadGenderTypes}
                                     name={ClientFields.GenderCode}
                                     fullWidth
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <AsyncSelect control={methods.control} label="Estado Civil"
                                     loadOptions={loadMaritalStatusTypes}
                                     name={ClientFields.MaritalStatusCode}
                                     fullWidth
                        />
                    </Grid>
                    <Grid item md={3}>
                        <ControlledDatePicker control={methods.control}
                                              label="Fecha Nacimiento"
                                              name={ClientFields.BirthDate}
                        />
                    </Grid>

                </React.Fragment>
            }

            {
                (client?.[ClientFields.ClientTypeCode] === ClientType.Juridica)
                &&
                <Grid item xs={6} container spacing={2}>
                    <Grid item xs={6}>
                        <AsyncSelect control={methods.control}
                                     name={ClientFields.ClientClassificationCode}
                                     defaultValue={client?.[ClientFields.ClientClassificationCode]}
                                     label="Tipo Societario" loadOptions={loadClientClassifications}
                                     fullWidth
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <ControlledTextField name={SolicitationClientFormDataFields.Participation}
                                             label='Participación'
                                             control={methods.control}
                                             fullWidth
                                             value={value}
                                             onChange={handleChange}
                                             error={!!error}
                                             helperText={error}
                                             InputProps={{
                                                 endAdornment: <InputAdornment position={'start'}>%</InputAdornment>
                                             }}
                        />
                    </Grid>
                </Grid>
            }
            <Grid item xs={4}>
                <ControlledTextField control={methods.control}
                                     style={{marginBottom: 0}}
                                     label={"Tipo Relación"}
                                     options={relationshipTypes} select
                                     name={ClientRelationshipViewFields.RelationshipTypeCode}
                                     fullWidth
                />
            </Grid>
            <Grid item xs={12} mt={5}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                    <Typography fontSize={16} fontWeight={600}>{`Domicilio ${client[ClientFields.ClientTypeCode] === ClientType.Fisica ? 'real' : 'legal'}`}</Typography>
                </Stack>
            </Grid>
            <Grid item xs={12}>
                <Divider/>
            </Grid>
            <Grid item xs={12} mt={1}>
                {
                    !loading &&
                        <FormProvider {...methods}>
                            <AddressFormContentData nameBase={CoOwnerRelationshipWithoutSolicitationFields.Address}/>
                        </FormProvider>
                }
            </Grid>
            {
                (watchMaritalStatus === MaritalStates.Casado || watchMaritalStatus === MaritalStates.Convivientes) &&
                <Grid item xs={12} container mt={2} spacing={2}>
                    <Grid item xs={12}>
                        <Typography fontSize={16} fontWeight={600}>Datos del cónyuge</Typography>
                    </Grid>
                    <Grid item xs={12}><Divider/></Grid>
                    <Grid item xs={12}>
                        {
                            !fiance &&
                            <Grid item xs={12} justifyContent={'center'} textAlign={'center'}>
                                <Typography mt={2}>{'Ingrese el CUIT del conyuge para sincronizar los datos con AFIP y agilizar la operación'}</Typography>
                                <Stack direction={'row'} alignItems={'center'} spacing={4} mt={2} justifyContent={'center'}>
                                    <TextField
                                        value={cuit}
                                        onChange={handleChangeCuit}
                                        size='small'
                                        label='CUIT/CUIL'
                                    />
                                    <Button color='primary' variant='contained' onClick={handleClick}>Validar</Button>
                                </Stack>
                                {
                                    loadingCuit &&
                                    <Stack alignItems={'center'} justifyContent={'center'} textAlign={'center'} pt={3} spacing={2}>
                                        <CircularProgress color="primary" size={40} value={40} />
                                        <Typography fontSize={14}>Aguarde mientras buscamos el CUIT/CUIL en la base de datos...</Typography>
                                    </Stack>
                                }
                            </Grid>
                        }
                    </Grid>
                    <Grid item xs={12}>
                        {
                            fiance &&
                            (
                                fiance[ClientFields.ClientTypeCode] === ClientType.Fisica ?
                                    <ClientSummaryDialogCard client={fiance}/>
                                    :
                                    <Alert severity={'error'}>No es posible cargar como cónyuge una empresa.</Alert>
                            )
                        }
                    </Grid>
                    <Grid item xs={12} textAlign={'center'}>
                        {!loadingCuit && fiance &&
                            <Button color='primary' size='small' variant={'contained'} onClick={() => {
                                setFiance(undefined)
                                onCancel()
                            }} endIcon={<ReplayIcon />}>
                                Recargar
                            </Button>
                        }
                    </Grid>
                </Grid>
            }
            <Grid item xs={12} mt={2}>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <Button color='secondary' variant='contained' onClick={onBack} startIcon={<Icon icon='mdi:chevron-left' fontSize={20} />}>Atrás</Button>
                    <Button color='primary' variant='contained' onClick={methods.handleSubmit(onSubmit)}>
                        Guardar
                    </Button>
                </Stack>
            </Grid>
            <DialogAddress
                open={editAddress}
                setEditAddress={setEditAddress}
                renderAddressTitle={() => `Editar domicilio ${client[ClientFields.ClientTypeCode] === ClientType.Fisica ? 'real' : 'legal'}`}
                address={addressInDialog && addressInDialog}
                onClose={onCloseDialog}
                onSubmitDialog={onSubmitDialog}
                clientId={client[EntityWithIdFields.Id]}
                addressType={client[ClientFields.ClientTypeCode] === ClientType.Fisica ? ClientAddressTypes.Real : ClientAddressTypes.Legal}
            />
        </Grid>
    )
}


export default CoOwnersDetailDialogForm